import { useState, useEffect } from "react";
import { Phone } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { ferdinandPhoto } from "@/assets/images";
import ContactModal from "@/components/ContactModal";
import { BookingLanguage } from "@/types/booking";
import { isBusinessHours } from "@/utils/businessHours";
import { useMarket } from "@/hooks/useMarket";
import { getSupportPhone } from "@/config/market";

interface SupportButtonProps {
  language?: BookingLanguage;
}

const SupportButton = ({ language = "en-US" }: SupportButtonProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [isLive, setIsLive] = useState(isBusinessHours);
  const isMobile = useIsMobile();
  const { market } = useMarket();
  // Null in markets that answer on WhatsApp only — they get the icon instead of
  // a foreign support line in the header (AHD-8731).
  const supportPhone = getSupportPhone(market);

  useEffect(() => {
    const interval = setInterval(() => setIsLive(isBusinessHours()), 60_000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <button
        onClick={() => setContactModalOpen(true)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`
          relative flex items-center gap-2 bg-white rounded-full pr-1 pl-4 py-1
          border transition-[border-color] duration-200
          ${isHovered ? "border-black/[0.08]" : "border-transparent"}
        `}
        aria-label="Contact support"
      >
        {/* Phone number or icon */}
        {isMobile || !supportPhone ? (
          <Phone className="h-5 w-5 text-neutral-900" />
        ) : (
          <span className="text-sm font-normal text-neutral-900 font-aeonik leading-none">
            {supportPhone}
          </span>
        )}

        {/* Avatar with online indicator */}
        <div className="relative">
          <div className="w-8 h-8 rounded-full overflow-hidden bg-neutral-100">
            <img
              src={ferdinandPhoto}
              alt="Ferdinand - Support"
              className="w-full h-full object-cover"
            />
          </div>
          {/* Online indicator — green during business hours, gray otherwise */}
          <div
            className={`absolute -bottom-0.5 -left-0.5 w-3 h-3 rounded-full ring-2 ring-white ${isLive ? "bg-deep-green-400" : "bg-neutral-300"}`}
          />
        </div>
      </button>

      <ContactModal
        open={contactModalOpen}
        onOpenChange={setContactModalOpen}
        language={language}
      />
    </>
  );
};

export default SupportButton;
